import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Loader2 } from "lucide-react";

interface CompanyHit { id: string; name: string; category: string | null }

interface Props {
  onAdd: (input: { companyId: string; note?: string }) => void;
  busy?: boolean;
}

export function LinkedCompaniesAddPopover({ onAdd, busy }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<CompanyHit | null>(null);
  const [note, setNote] = useState("");

  const search = useQuery<CompanyHit[]>({
    queryKey: ["companies-search", q],
    queryFn: async () => {
      if (q.length < 2) return [];
      const r = await fetch(`/api/search?q=${encodeURIComponent(q)}`, { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data?.companies) ? data.companies : [];
    },
    enabled: q.length >= 2,
  });

  const reset = () => { setQ(""); setPicked(null); setNote(""); };
  const submit = () => {
    if (!picked) return;
    onAdd({ companyId: picked.id, note: note.trim() || undefined });
    reset();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
          <Plus className="w-3 h-3" /> Add
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 space-y-2">
        {!picked ? (
          <>
            <Input placeholder="Search companies..." value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <div className="max-h-48 overflow-y-auto -mx-1">
              {search.isFetching && <div className="p-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Searching…</div>}
              {(search.data ?? []).map((c) => (
                <button key={c.id}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm flex items-center justify-between"
                  onClick={() => setPicked(c)}>
                  <span>{c.name}</span>
                  {c.category && <span className="text-xs text-muted-foreground">{c.category}</span>}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-medium">{picked.name}</div>
            <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={reset}>Back</Button>
              <Button size="sm" onClick={submit} disabled={busy}>{busy ? "Adding…" : "Add"}</Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
